// What a leader sees the first time they log in after being handed a section.
//
// This is the piece that makes the checklist — not the tour — the spine of
// onboarding. The tour still runs, but as a preamble to work that person owns,
// scoped to just their pages. They never see the admin's full list, and they're
// never walked through fifteen modules they weren't asked about.

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useSetup } from '@/contexts/SetupContext';
import { setupChecklistService } from '@/services/setupChecklistService';
import { setupService } from '@/services/setupService';
import { SETUP_SECTIONS_BY_ID } from '@/lib/setupChecklist';

// Never interrupt someone who's already inside setup — they've clearly found it.
const SUPPRESSED_PREFIXES = ['/setup', '/getting-started', '/login', '/chat'];

export default function AssigneeIntroDialog() {
  const { user } = useAuth();
  const { run, startSectionTour } = useSetup();
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const suppressed = SUPPRESSED_PREFIXES.some((p) => location.pathname.startsWith(p));

  const { data } = useQuery({
    queryKey: ['setup-my-assignments'],
    queryFn: setupChecklistService.myAssignments,
    enabled: Boolean(user) && !suppressed,
    staleTime: 5 * 60_000
  });

  // One section at a time. Finishing one surfaces the next on the following
  // load, which keeps the ask small — "here's your bit", not "here are four".
  const nextSectionId = data?.needsIntro?.[0];
  const section = nextSectionId ? SETUP_SECTIONS_BY_ID[nextSectionId] : undefined;

  // A run in flight means they're already touring; don't stack a dialog on it.
  if (!section || dismissed || suppressed || run) return null;

  const toChecklist = () => navigate(`/setup?section=${section.id}`);

  const begin = () => {
    setupService.trackEvent('assignee_intro_accepted', section.id);
    setDismissed(true);
    // startSectionTour already falls back to the checklist when the section has
    // no module to tour, so this is one call either way.
    if (section.module) startSectionTour(section.id, section.module);
    else toChecklist();
  };

  // Skipping is a first-class outcome, not a failure. Someone who already knows
  // the app shouldn't be walled behind a walkthrough to reach four checkboxes.
  const skip = () => {
    setupService.trackEvent('assignee_intro_skipped', section.id);
    setDismissed(true);
    setupChecklistService.markTourDone(section.id).catch(() => {});
    toChecklist();
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) setDismissed(true); }}>
      <DialogContent className="max-w-md gap-0 overflow-hidden rounded-2xl p-0 sm:rounded-2xl [&>button[aria-label=Close]]:text-white">
        <div className="relative bg-gradient-to-br from-[#E51636] via-[#cf1330] to-[#8f0d21] px-6 pb-6 pt-7 text-white">
          <div className="pointer-events-none absolute -right-12 -top-14 h-44 w-44 rounded-full bg-white/10" />
          <div className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15">
            <ClipboardCheck className="h-6 w-6" />
          </div>
          <DialogTitle className="relative mt-3.5 text-[22px] font-bold leading-tight tracking-tight text-white">
            You've been given {section.label}
          </DialogTitle>
          <DialogDescription className="relative mt-1.5 text-[15px] leading-relaxed text-white/85">
            {section.module
              ? "We'll walk you through these pages first, then you'll have a short checklist to set it up your way."
              : "There's a short checklist to work through — nothing to learn first."}
          </DialogDescription>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm leading-relaxed text-gray-500">{section.blurb}</p>
          <p className="mt-3 text-sm text-gray-400">
            {section.tasks.length} {section.tasks.length === 1 ? 'step' : 'steps'}
            {' · you can stop and come back any time'}
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 px-6 pb-5">
          <button
            className="text-xs font-medium text-gray-400 transition-colors hover:text-gray-600"
            onClick={skip}
          >
            Skip to my checklist
          </button>
          <Button
            className="h-11 rounded-xl bg-[#E51636] px-6 font-semibold text-white shadow-md shadow-[#E51636]/30 hover:bg-[#c91230]"
            onClick={begin}
          >
            {section.module ? (<><Sparkles className="mr-1.5 h-4 w-4" /> Show me around</>) : 'Get started'}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
